import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import api, { readerApi, reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import GoalProgressBars from '../components/progress/GoalProgressBars';
import BibleProgressCalendar from '../components/progress/BibleProgressCalendar';
import CommunityHero from '../components/progress/CommunityHero';
import ChallengeGoals from '../components/progress/ChallengeGoals';
import LiveTicker from '../components/progress/LiveTicker';
import QuickLog from '../components/log/QuickLog';
import SeasonInvitation from '../components/progress/SeasonInvitation';
import useSeason from '../hooks/useSeason';
import { seasonState } from '../utils/season';
import './HomePage.css';
import ReadingPulse from '../components/progress/ReadingPulse';
import YearSelector from '../components/progress/YearSelector';

/**
 * Home is the community read-through plus one-tap logging (WS1 + WS2).
 *
 * The hero states where the community is, the map shows it chapter by chapter
 * with the still-needed chapters highlighted, and the log surface sits right
 * under it so a returning reader is one tap from done.
 */
const HomePage = () => {
  const { user, isAuthenticated } = useAuth();
  const season = useSeason();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const { lastGlobalStatsUpdate, lastReadingReportCreated } = useWebSocket();
  const [globalStats, setGlobalStats] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadStatsTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const statsRequestRef = useRef(0);
  const CACHE_DURATION = 300000; // 5 minutes cache duration

  const loadGlobalStats = useCallback(async (_retryCount = 0, { force = false } = {}) => {
    const requestId = ++statsRequestRef.current;
    try {
      // Check if we have recent data to avoid unnecessary API calls
      const now = Date.now();
      if (!force && lastFetchTimeRef.current && (now - lastFetchTimeRef.current) < CACHE_DURATION) {
        console.log('Using cached global stats');
        setLoading(false);
        return;
      }

      const response = await api.get(`/challenges/years/${year}/stats`);
      if (requestId !== statsRequestRef.current) return;
      setGlobalStats(response.data.stats);
      lastFetchTimeRef.current = now;
      setError(null);
    } catch (err) {
      if (requestId !== statsRequestRef.current) return;
      console.error('Failed to load global stats:', err);

      // If we've exhausted retries or it's not a rate limit error, show error
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment and try again.');
      } else {
        setError('Failed to load statistics. Please try again.');
      }
    } finally {
      if (requestId === statsRequestRef.current) setLoading(false);
    }
  }, [year]);

  const loadCompletion = useCallback(async () => {
    try {
      const response = await reportApi.getGlobalStats();
      setCompletion(response.data.stats);
    } catch (err) {
      // Not critical — the hero still works from global stats.
      console.error('Failed to load next completion chapters:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    lastFetchTimeRef.current = 0;
    setGlobalStats(null);
    loadGlobalStats(0, { force: true });
    loadCompletion();

    const requestCounter = statsRequestRef;
    return () => {
      requestCounter.current++;
      if (loadStatsTimeoutRef.current) {
        clearTimeout(loadStatsTimeoutRef.current);
      }
    };
  }, [loadGlobalStats, loadCompletion]);

  // The log surface needs the user's readers.
  useEffect(() => {
    if (!isAuthenticated) return;
    readerApi.getReaders()
      .then(response => setReaders(response.data.readers || []))
      .catch(err => console.error('Failed to load readers:', err));
  }, [isAuthenticated]);

  // Live stats: a log by anyone repaints the map without a reload.
  useEffect(() => {
    if (lastGlobalStatsUpdate?.stats) {
      loadGlobalStats(0, { force: true });
    }
  }, [lastGlobalStatsUpdate, loadGlobalStats]);

  // The still-needed list shifts as chapters get read; refresh it behind the
  // debounced stats broadcast rather than on every single report event.
  useEffect(() => {
    if (!lastReadingReportCreated) return undefined;
    const timer = setTimeout(loadCompletion, 2500);
    return () => clearTimeout(timer);
  }, [lastReadingReportCreated, loadCompletion]);

  const neededChapterIds = useMemo(() => {
    if (!globalStats?.chaptersNeeded) return null;
    return new Set(globalStats.chaptersNeeded.map(chapter => chapter.id));
  }, [globalStats]);

  const handleLogged = useCallback(() => {
    window.dispatchEvent(new Event('reading-progress-changed'));
    // Our own log should show up immediately, not after the socket debounce.
    loadGlobalStats(0, { force: true });
    loadCompletion();
  }, [loadGlobalStats, loadCompletion]);

  const retry = () => {
    if (loadStatsTimeoutRef.current) {
      clearTimeout(loadStatsTimeoutRef.current);
    }
    loadStatsTimeoutRef.current = setTimeout(() => {
      loadGlobalStats(0, { force: true });
    }, 1000);
  };

  const challenge = globalStats?.challenge || completion?.challenge || null;

  return (
    <div className={`home-page ${isAuthenticated ? 'home-page--member' : ''}`}>
      {isAuthenticated && <div className="pulse-welcome"><p className="season-eyebrow">A LITTLE READING. A SHARED PURPOSE.</p><h1>Your reading makes a difference.</h1><p>Find your place in Scripture. See what we’re building together.</p></div>}
      {isAuthenticated ? <ReadingPulse /> : <SeasonInvitation season={season} isAuthenticated={false} />}
      {isAuthenticated && <section id="quick-log" className="home-log-section" aria-label="Log your reading">
        {readers.length > 0 ? <QuickLog readers={readers} userId={user?.id} onLogged={handleLogged} />
          : <p>Ready to record your reading? <Link to="/report">Open the reading log</Link>.</p>}
      </section>}
      <div className="home-community">
      <div className="home-section-heading"><p className="season-eyebrow">EVERY CHAPTER COUNTS</p><h2>A whole Bible. A shared story.</h2><p>Each reading helps our community reach every chapter of Scripture.</p></div>
      {loading && <LoadingSpinner size="large" message="Loading community progress…" />}
      {error && <div className="error-message" role="status"><h2>Community progress is taking a moment</h2><p>{error} You can still join or read using the links above.</p><button onClick={retry} className="retry-button">Try again</button></div>}
      {seasonState(season)?.phase === 'upcoming' && season?.goals?.length > 0 && <section className="season-targets" aria-labelledby="season-targets-title">
        <div><p className="season-eyebrow">WHAT WE’RE READING TOWARD</p><h2 id="season-targets-title">Our 2026 goals</h2><p>Season progress begins on opening day. Until then, every reading builds our all-time story.</p></div>
        <div className="season-target-grid">{[...season.goals].sort((a, b) => a.displayOrder - b.displayOrder).map(goal => <div key={goal.id}><strong>{Number(goal.targetValue).toLocaleString()}</strong><span>{goal.name}</span></div>)}</div>
      </section>}
      {/* Where the community is */}
      <YearSelector value={year} onChange={setYear} earliestYear={globalStats?.earliestYear} />
      {globalStats?.year === year && <CommunityHero key={year} stats={globalStats} />}

      {/* Season goals, measured inside the challenge window */}
      {challenge && completion?.challengeGoals?.length > 0 && (
        <ChallengeGoals
          goals={completion.challengeGoals}
          challengeName={challenge.name}
        />
      )}

      <LiveTicker />

      {/* Goal Progress Bars */}
      {completion?.goals && completion.goals.length > 0 && (
        <details className="home-lifetime-goals"><summary>Our all-time community goals</summary><GoalProgressBars goals={completion.goals} /></details>
      )}

      </div>
      {/* The community's Bible, chapter by chapter */}
      <div className="global-progress-section" id="community-map">
        <div className="section-header">
          <p className="season-eyebrow">{year === 'all' ? 'ALL-TIME COMMUNITY PROGRESS' : `${year} COMMUNITY PROGRESS`}</p>
          <h2>Explore our shared Bible</h2>
          <p>
            {neededChapterIds && neededChapterIds.size > 0
              ? `${neededChapterIds.size} highlighted chapters are all that stand between us and reading the Bible through again.`
              : 'See which chapters of the Bible have been read by our community'}
          </p>
        </div>

        {globalStats?.chapters && globalStats?.chapterReadCounts && (
          <details className="home-map-details" open={isAuthenticated || undefined}>
            <summary>Explore all 66 books and 1,189 chapters</summary>
            <BibleProgressCalendar
              chapters={globalStats.chapters}
              chapterReadCounts={globalStats.chapterReadCounts}
              neededChapterIds={neededChapterIds}
            />
          </details>
        )}
      </div>

      {/* Additional CTA for unauthenticated users */}
      {!isAuthenticated && (
        <div className="bottom-cta-section">
          <div className="cta-content">
            <h2>There’s a place for you in this story.</h2>
            <p>Read on your own, gather your family, or bring your church. Start with a chapter and keep going together.</p>
            <Link to="/auth?view=register" className="season-button">
              Create an account
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
